import { AgGridTable } from 'components/AgGrid/AgGridTable';
import { ModalDialog } from 'components/ModalDialog';
import type { BimPriceLevelEnum, BimWorkOrderPriceData } from 'gql/graphql';
import type { AnyObject } from 'types/AnyObject';
import { cnMixSpace } from 'uikit';

import type { ColDef, ColGroupDef, RowClassRules } from 'ag-grid-community';
import type React from 'react';
import { useMemo } from 'react';

import { BimModalTitleWithPriceLevel } from '../../BimCommonComponents/BimModalTitleWithPriceLevel';
import { useBimWorkOrderPriceVersionRevisions } from '../../WorkOrderVersion/BimWorkOrderPriceVersionTable/hooks/useBimWorkOrderPriceVersionRevisions';
import type { IBimWorkOrderRowDetails } from '../BimWorkOrderPriceTable/cellRenderers';
import { getBimWorkOrderRowClass } from '../BimWorkOrderPriceTable/cellRenderers';
import { useBimWorkOrderPriceRevisions } from '../BimWorkOrderPriceTable/hooks/useBimWorkOrderPriceRevisions';
import { BimWorkOrderRowDetailsType } from '../types';

import { useBimWorkOrderPriceRevisionsColumnsByType } from './useBimWorkOrderPriceRevisionsColumnsByType';

interface IBimWorkOrderPriceRevisions {
    baseRow: AnyObject[];
    columns: (ColDef | ColGroupDef)[];
    filterValues: Record<string, string[]>;
    changeFilterHandler: (params: { fieldName: string; checkedValues: string[] }) => void;
    onClose: () => void;
    defaultColDef?: ColDef;
    rowClassRules?: RowClassRules;
    styleClass?: string;
    selectedRow: IBimWorkOrderRowDetails<BimWorkOrderPriceData>;
    isVersionPage?: boolean;
    priceLevel: BimPriceLevelEnum | undefined;
}

export const BimWorkOrderPriceRevisions: React.FC<IBimWorkOrderPriceRevisions> = (props) => {
    const {
        columns,
        styleClass,
        defaultColDef,
        selectedRow,
        onClose,
        baseRow,
        filterValues,
        changeFilterHandler,
        isVersionPage,
        priceLevel,
    } = props;
    const { detailsType, data } = selectedRow;

    const modalTitle = useMemo(
        () =>
            detailsType === BimWorkOrderRowDetailsType.Revision
                ? `Ревизии ${data.planValue.code}`
                : `Обновления по смете ${data.planValue.code}`,
        [data.planValue.code, detailsType],
    );

    const columnsByType = useBimWorkOrderPriceRevisionsColumnsByType({
        detailsType,
        columns,
        workOrderPriceId: data.id,
        changeFilterHandler,
        isVersionPage,
    });

    const { bimWorkOrderPriceRevisionsDataSource } = useBimWorkOrderPriceRevisions(
        data.id,
        detailsType,
        filterValues,
        priceLevel,
    );
    const { bimWorkOrderPriceVersionRevisionsDataSource } = useBimWorkOrderPriceVersionRevisions(
        data.id,
        detailsType,
        filterValues,
        priceLevel,
    );

    const currentDataSource = useMemo(
        () => (isVersionPage ? bimWorkOrderPriceVersionRevisionsDataSource : bimWorkOrderPriceRevisionsDataSource),
        [bimWorkOrderPriceRevisionsDataSource, bimWorkOrderPriceVersionRevisionsDataSource, isVersionPage],
    );

    return (
        <ModalDialog
            isOpen
            title={<BimModalTitleWithPriceLevel priceLevel={priceLevel} modalTitle={modalTitle} />}
            onClose={onClose}
            dockedToWindow="bottom"
            withTitleCloseButton
            isOnClickOutsideDisable
            withBottomButtons={false}
        >
            <div
                className={`ag-theme-alpine ${styleClass} ${cnMixSpace({
                    pV: 's',
                    pH: 'l',
                })}`}
            >
                <AgGridTable
                    pinnedTopRowData={detailsType === BimWorkOrderRowDetailsType.Revision ? baseRow : undefined}
                    datasource={currentDataSource}
                    columnDefs={columnsByType}
                    rowModelType="infinite"
                    tooltipShowDelay={200}
                    maxConcurrentDatasourceRequests={1}
                    maxBlocksInCache={50}
                    cacheBlockSize={50}
                    defaultColDef={defaultColDef}
                    rowClassRules={getBimWorkOrderRowClass}
                />
            </div>
        </ModalDialog>
    );
};

BimWorkOrderPriceRevisions.displayName = 'BimWorkOrderPriceRevisions';
